import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { getSiteKey } from '@/utils/siteKey';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface LeadData {
  name: string;
  email: string;
  phone?: string;
  interest_type?: string;
  preferred_dates?: string;
  num_adults?: number;
  num_children?: number;
  notes?: string;
}

interface UseEventChatbotOptions {
  eventId?: string;
  eventName?: string;
  routeContext?: {
    table?: string;
    slug?: string;
    pathname?: string;
  };
}

export function useEventChatbot(options: UseEventChatbotOptions = {}) {
  const { eventId, eventName, routeContext } = options;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const { toast } = useToast();
  const abortControllerRef = useRef<AbortController | null>(null);
  const { isAdmin, loading: adminLoading } = useIsAdmin();

  const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();

    try {
      const messagesToSend = [...messages, userMessage].map(m => ({
        role: m.role,
        content: m.content,
      }));

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/event-chatbot`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            messages: messagesToSend,
            eventId,
            conversationId,
            siteKey: getSiteKey(),
            adminMode: isAdmin,
            adminLoading,
            routeContext,
          }),
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      // Stream the response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';
      const assistantId = generateId();

      // Add empty assistant message
      setMessages(prev => [
        ...prev,
        { id: assistantId, role: 'assistant', content: '', timestamp: new Date() },
      ]);

      let textBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });

        // Process line-by-line
        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') continue;

          try {
            const parsed = JSON.parse(jsonStr);
            const deltaContent = parsed.choices?.[0]?.delta?.content;
            if (deltaContent) {
              assistantContent += deltaContent;
              setMessages(prev =>
                prev.map(m =>
                  m.id === assistantId ? { ...m, content: assistantContent } : m
                )
              );
            }
          } catch {
            // Incomplete JSON, put it back
            textBuffer = line + '\n' + textBuffer;
            break;
          }
        }
      }

      // Check for lead capture triggers in the response
      const leadTriggers = [
        'contact you',
        'share your email',
        'contact details',
        'reach out',
        'book a spot',
        'reserve',
        'sign up',
      ];
      
      const shouldShowLeadForm = leadTriggers.some(trigger =>
        assistantContent.toLowerCase().includes(trigger)
      );

      if (shouldShowLeadForm) {
        setShowLeadForm(true);
      }

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Request cancelled');
        return;
      }

      console.error('Chat error:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Something went wrong';
      
      toast({
        title: 'Chat Error',
        description: errorMessage,
        variant: 'destructive',
      });

      // Add error message
      setMessages(prev => [
        ...prev,
        {
          id: generateId(),
          role: 'assistant',
          content: "I'm sorry, I'm having trouble responding right now. Please try again or contact us directly at hello@nomadfest.com",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [messages, eventId, conversationId, isLoading, isAdmin, adminLoading, routeContext, toast]);

  const submitLead = useCallback(async (leadData: LeadData) => {
    try {
      // Generate conversation summary from messages
      const conversationSummary = messages
        .slice(-6)
        .map(m => `${m.role}: ${m.content.substring(0, 100)}`)
        .join('\n');

      const { error } = await supabase
        .from('chat_leads')
        .insert({
          name: leadData.name,
          email: leadData.email,
          phone: leadData.phone || null,
          interest_type: leadData.interest_type || eventName || 'General Inquiry',
          preferred_dates: leadData.preferred_dates || null,
          num_adults: leadData.num_adults || null,
          num_children: leadData.num_children || null,
          notes: leadData.notes || null,
          conversation_summary: conversationSummary,
          source_page: window.location.href,
          event_id: eventId || null,
        });

      if (error) throw error;

      setShowLeadForm(false);
      
      toast({
        title: 'Thank you! 🎉',
        description: 'Our team will reach out to you soon!',
      });

      // Add confirmation message
      setMessages(prev => [
        ...prev,
        {
          id: generateId(),
          role: 'assistant',
          content: `Thanks ${leadData.name}! I've shared your details with our team. They'll reach out to you at ${leadData.email} very soon. In the meantime, feel free to ask me any other questions! 🎉`,
          timestamp: new Date(),
        },
      ]);

    } catch (error) {
      console.error('Lead submission error:', error);
      toast({
        title: 'Submission Error',
        description: 'Failed to submit your details. Please try again.',
        variant: 'destructive',
      });
    }
  }, [messages, eventId, eventName, toast]);

  const cancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsLoading(false);
    }
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setShowLeadForm(false);
  }, []);

  return {
    messages,
    isLoading,
    showLeadForm,
    setShowLeadForm,
    sendMessage,
    submitLead,
    cancelRequest,
    clearMessages,
    isAdmin,
  };
}
